import type { FC } from "react";

import type { BacklinksFollowType, BacklinksQueryStatusType } from "@similarweb/pro-api-client";
import { Checkbox, TextSwitcherItem } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useExpectedBacklinksParams } from "app/pages/digital-marketing/backlinks/shared/BacklinksUtils";
import { allTrackers } from "app/services/track/track";
import type { IRangeValues } from "pro-features/components/filtersPanel/src/RangeFilter";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { DTSFilter } from "../../../domains/components/filters/dts-range";

import { BacklinksFilterSwitcherStyled } from "./backlinks-filters.styled";
import BacklinksIncludeExcludeFilter from "./include-exclude-filter/include-exclude-filter.component";

const BACKLINKS_FILTER_STATUS: Array<BacklinksQueryStatusType> = ["All", "Active", "New", "Lost"];
const BACKLINKS_FILTER_FOLLOW: Array<BacklinksFollowType> = ["All", "DoFollowOnly", "NoFollowOnly"];

const BACKLINKS_FILTER_STATUS_MAP: Record<BacklinksQueryStatusType, string> = {
    All: "digitalMarketing.backlinks.filters.all",
    Active: "digitalMarketing.backlinks.filters.active",
    Lost: "digitalMarketing.backlinks.filters.lost",
    New: "digitalMarketing.backlinks.filters.new",
};

const BACKLINKS_FILTER_FOLLOW_MAP: Record<BacklinksFollowType, string> = {
    All: "digitalMarketing.backlinks.filters.all",
    DoFollowOnly: "digitalMarketing.backlinks.filters.doFollow",
    NoFollowOnly: "digitalMarketing.backlinks.filters.noFollow",
};

function getAutomationId(status: BacklinksQueryStatusType | BacklinksFollowType): string {
    return `backlinks-table-filter-${status}`;
}

export const BacklinksTableFilters: FC = () => {
    const params = useExpectedBacklinksParams();
    const translate = useTranslation();

    const statusIndex = BACKLINKS_FILTER_STATUS.indexOf(params?.status ?? "All");
    const followIndex = BACKLINKS_FILTER_FOLLOW.indexOf(params?.follow ?? "All");
    const dtsFrom = params?.dtsFrom ?? "";
    const dtsTo = params?.dtsTo ?? "";
    const brokenLinksSelected = params?.brokenOnly === "true";

    const handleDtsFilter = (value: IRangeValues) => {
        void proNavigator.updateParams({ dtsFrom: value.fromValue, dtsTo: value.toValue }, { location: "replace" });
    };

    const handleStatusChange = (index: number) => {
        const status = BACKLINKS_FILTER_STATUS[index];
        const event = BACKLINKS_FILTER_STATUS_MAP[status];
        void proNavigator.updateParams({ status }, { location: "replace" });
        allTrackers.dispatchTrackEvent("Filter Table", "Click", `New - lost/${translate(event)}`);
    };

    const handleFollowChange = (index: number) => {
        const follow = BACKLINKS_FILTER_FOLLOW[index];
        const event = BACKLINKS_FILTER_FOLLOW_MAP[follow];
        void proNavigator.updateParams({ follow }, { location: "replace" });
        allTrackers.dispatchTrackEvent("Filter Table", "Click", `DoFollow - NoFollow/${translate(event)}`);
    };

    const handleCheckboxClick = () => {
        const toggleOption = !brokenLinksSelected;
        void proNavigator.updateParams({ brokenOnly: toggleOption || null }, { location: "replace" });
        allTrackers.dispatchTrackEvent("Filter Table", "Click", `Broken Only ${toggleOption ? "Off" : "On"}`);
    };

    return (
        <Flex
            flexDirection="row"
            gap="8px"
            alignItems="center"
            overflow="visible"
        >
            <BacklinksFilterSwitcherStyled
                selectedIndex={statusIndex}
                onItemClick={handleStatusChange}
            >
                {BACKLINKS_FILTER_STATUS.map((status) => (
                    <TextSwitcherItem
                        key={status}
                        data-automation={getAutomationId(status)}
                    >
                        {translate(BACKLINKS_FILTER_STATUS_MAP[status])}
                    </TextSwitcherItem>
                ))}
            </BacklinksFilterSwitcherStyled>
            <BacklinksFilterSwitcherStyled
                selectedIndex={followIndex}
                onItemClick={handleFollowChange}
            >
                {BACKLINKS_FILTER_FOLLOW.map((follow) => (
                    <TextSwitcherItem
                        key={follow}
                        data-automation={getAutomationId(follow)}
                    >
                        {translate(BACKLINKS_FILTER_FOLLOW_MAP[follow])}
                    </TextSwitcherItem>
                ))}
            </BacklinksFilterSwitcherStyled>
            <DTSFilter
                onChange={handleDtsFilter}
                tableFilters={{ fromValue: dtsFrom, toValue: dtsTo }}
                minFromValue={0}
                maxFromValue={100}
                minToValue={parseInt(dtsFrom, 10) || 0}
                maxToValue={100}
                tooltipText="digitalsuite.backlinks.table.column.domain_score.description"
                filterTextKey="digitalsuite.backlinks.table.column.domain_score"
                selectedText={{
                    VOLUME: "DS",
                    AT_LEAST: "DS >",
                    UP_TO: "DS <",
                }}
            />
            <BacklinksIncludeExcludeFilter />
            <Checkbox
                onClick={handleCheckboxClick}
                selected={brokenLinksSelected}
                label={translate("digitalsuite.backlinks.table.filters.brokenBacklinks")}
            />
        </Flex>
    );
};
